import { Tour, TourProps } from 'antd'
import * as React from 'react'
import { useIntl } from 'react-intl'

import { TourContext } from '../TourProviderContext'
import { useWorkspaceContext } from '../WorkspaceContext'
import { TOURS_CONFIGURATION } from '@/config/tours'
import { Action, useReducerWithLocalStorage } from '@/reducers/commons'
import messages, { buildI18nFunction, IntlFunction } from '@/utils/messages'

type TourProviderProps = {
  children: React.ReactNode
}

const COMPONENT_ID = 'Tour'

type ActionTypes = 'toggleTourStatus' | 'toggleGeneralStatus'

// The reducer State has a global enabled flag, if it is false the feature is off
// A property tours to track tours individually and it has as many flags as the tours in TOURS_CONFIGURATION
// By DEFAULT_STATE all tours are undefined which means they were not seen just yet
export type State = {
  enabled: boolean
  tours: {
    [Tour in keyof typeof TOURS_CONFIGURATION]?: boolean
  }
}

const DEFAULT_STATE = {
  enabled: true,
  tours: {},
}

function tourReducer(
  state: State,
  action: {
    type: ActionTypes
    payload?: { tourKey: string; flag?: boolean }
  }
): State {
  let newState: State

  const { type, payload } = action

  switch (type) {
    case 'toggleTourStatus':
      newState = {
        enabled: true,
        tours: { ...state.tours, [payload.tourKey]: payload.flag },
      }
      break

    case 'toggleGeneralStatus': {
      newState = {
        enabled: !state.enabled,
        tours: {
          ...Object.keys(state.tours).reduce(
            (accum, currentValue) => {
              accum = { ...accum, [currentValue]: state.enabled }
              return accum
            },
            {} as State['tours']
          ),
        },
      }
      break
    }

    default:
      newState = DEFAULT_STATE
  }

  return newState
}

export function TourProvider({ children }: TourProviderProps): JSX.Element {
  const t = buildI18nFunction(useIntl().formatMessage)
  const { workspaceId } = useWorkspaceContext()

  const { dispatch, state } = useReducerWithLocalStorage<ActionTypes, State>({
    componentId: COMPONENT_ID,
    initialState: DEFAULT_STATE,
    reducer: tourReducer,
    workspaceId,
  })

  const handleSingleTourToggle = (flag: boolean, tourKey: string) => {
    dispatch({ type: 'toggleTourStatus', payload: { tourKey, flag } })
  }

  const handleGeneralStatusToggle = () => {
    dispatch({ type: 'toggleGeneralStatus', payload: {} })
  }

  const tours = initializeTours(dispatch, state, t, TOURS_CONFIGURATION)

  return (
    <TourContext.Provider
      value={{
        tours,
        onGeneralStatusToggle: handleGeneralStatusToggle,
        onSingleTourToggle: handleSingleTourToggle,
        state,
      }}
    >
      {children}
    </TourContext.Provider>
  )
}

function initializeTours(
  dispatch: (value: Action<ActionTypes>) => void,
  state: State,
  t: IntlFunction,
  toursConfiguration: typeof TOURS_CONFIGURATION
): typeof TOURS_CONFIGURATION {
  return Object.keys(toursConfiguration).reduce(
    (outcome, tourKey) => {
      const currentTourConfiguration = toursConfiguration[tourKey]
      const hasTourBeenViewed = state.tours?.[tourKey]
      const steps = Object.keys(currentTourConfiguration.steps).reduce(
        (accum, stepKey) => {
          const ref = React.createRef<HTMLSpanElement>()
          const currentStepConfiguration =
            currentTourConfiguration.steps[stepKey]
          accum[stepKey] = {
            ...currentStepConfiguration,
            description: t(
              `${COMPONENT_ID}.${tourKey}.${stepKey}.description` as keyof typeof messages
            ),
            elementRef: ref,
            target: () => currentStepConfiguration.hasTarget && ref.current,
            title: t(
              `${COMPONENT_ID}.${tourKey}.${stepKey}.title` as keyof typeof messages
            ),
            TargetWrapper: (props: { children: JSX.Element }) => {
              return <span ref={ref}>{props.children}</span>
            },
          }
          return accum
        },
        {}
      )
      outcome[tourKey] = {
        ...currentTourConfiguration,
        Tour: (props: TourProps) =>
          hasTourBeenViewed ? null : (
            <Tour
              arrow={false}
              onClose={() => {
                dispatch({
                  payload: {
                    tourKey,
                    flag: true,
                  },
                  type: 'toggleTourStatus',
                })
              }}
              open={true}
              steps={Object.values(steps)}
              {...props}
            />
          ),
        steps,
      }

      return outcome
    },
    {} as typeof TOURS_CONFIGURATION
  )
}
